/**
 * §7.4 個人設定（新增）：改密碼、看自己的登入紀錄。
 *
 * §5.6：一次性密碼登入後 `must_change_password = true`，後端會把其他 API 全部擋成
 * `PASSWORD_CHANGE_REQUIRED`，所以這個畫面是強制的第一站（§7.1 的 home 會指到這裡）。
 */

import { useState } from 'react'
import { ApiError, api } from '../api/client.js'
import { Alert, Button, Card, Field, TextInput } from '../components/ui.js'
import { useAuth } from './AuthContext.js'
import { navigate } from '../router.js'

export function PasswordChangeScreen() {
  const { me, reload } = useAuth()
  const forced = me?.user.mustChangePassword === true
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (next !== again) { setError('兩次輸入的新密碼不一樣。'); return }
    setBusy(true)
    setError(null)
    try {
      await api.post('/api/auth/password', { currentPassword: current, newPassword: next })
      setDone(true)
      await reload()
      if (forced) navigate(me?.home && me.home !== '/me/password' ? me.home : '/')
    } catch (err) {
      // §5.6 的密碼品質訊息會說明為什麼不行（太短、與 email 相似…），直接顯示
      setError(err instanceof ApiError ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mx-auto max-w-md space-y-4">
      {forced && (
        <Alert tone="warn" title="請先設定新密碼">
          您目前使用的是一次性密碼。設定新密碼之後才能使用系統的其他功能。
        </Alert>
      )}
      <Card title="變更密碼">
        <form onSubmit={submit} className="space-y-3">
          <Field label="目前的密碼" required>
            <TextInput type="password" autoComplete="current-password" autoFocus
              value={current} onChange={(e) => setCurrent(e.target.value)} />
          </Field>
          <Field
            label="新密碼"
            required
            hint="至少 12 個字元。不要求大小寫與符號——長一點的句子就很夠用，也比較記得住。"
          >
            <TextInput type="password" autoComplete="new-password"
              value={next} onChange={(e) => setNext(e.target.value)} />
          </Field>
          <Field label="再輸入一次" required>
            <TextInput type="password" autoComplete="new-password"
              value={again} onChange={(e) => setAgain(e.target.value)} />
          </Field>
          {error && (
            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          )}
          {done && !forced && (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              密碼已變更。您在其他裝置上的登入已被登出。
            </div>
          )}
          <Button type="submit" variant="primary" size="lg" className="w-full" disabled={busy}>
            {busy ? '請稍候…' : '變更密碼'}
          </Button>
        </form>
      </Card>
    </div>
  )
}
